import {
    Chart,
    Chart21,
    Graph,
    Speedometer,
    Grid2,
    Radar2,
    Category,
    Diagram,
    RowVertical,
    type IconComponent,
} from '@/components/icons';
import {
    BarsThumb,
    BulletThumb,
    ClassificationThumb,
    GaugeThumb,
    LineThumb,
    NormalCurveThumb,
    QuadrantThumb,
    RadarThumb,
    ScatterThumb,
} from './thumbnails';
import XyScatterConfigPanel from './XyScatterConfigPanel';
import type { ComponentType, FC, SVGProps } from 'react';

/**
 * Front UI chart-type registry (WS-D).
 *
 * The single source of truth for what chart types the modal offers and how it
 * behaves per type. Each type is one cohesive, additive declaration — adding a
 * type is a new entry here (+ its mirrored-core GEOMETRY/EXTRACT entry), never an
 * edit to `ChartConfigModal`'s conditionals. This is the front half of the seam
 * that lets WS-E geometries appear and behave correctly in the picker.
 *
 * Kept lean on purpose: today's four types share one data contract
 * (`categorical-series`: an x column + N numeric y columns), so a
 * `requirements`-driven column-picker abstraction is intentionally NOT built.
 * The `ConfigPanel` seam below is declared but unused until a WS-E geometry
 * needs extra per-type controls.
 */

/** Chart types backed by a geometry today (subset of `report-payload.ts`'s `ChartType`). */
export type ChartTypeId =
    | 'bar'
    | 'line'
    | 'radar'
    | 'scatter'
    | 'xy_scatter'
    | 'distribution'
    | 'gauge'
    | 'bullet'
    | 'level_bars';

/** Capability flags that drive the modal's shared controls (no per-type switch). */
export interface ChartTypeSupports {
    /** 2+ y columns → 2+ series (checkbox list vs single pick). */
    multiSeries: boolean;
    /** Reference bands / cutoff lines / forced y-scale (cartesian only). */
    decorations: boolean;
    /** Optional single-series legend-label override. */
    seriesLabel: boolean;
    /** Optional group column → two-level x-axis (CBCL scale groups, Big-Five domínios). */
    grouping?: boolean;
}

/** Editable quadrant divider values (strings so inputs can be cleared). */
export interface QuadrantEditorValue {
    x: string;
    y: string;
}

/** Editable axis-extreme labels (strings so inputs can be cleared). */
export interface AxisEndLabelsEditorValue {
    x_min: string;
    x_max: string;
    y_min: string;
    y_max: string;
}

/**
 * Props handed to an optional per-type `ConfigPanel`. The modal owns the state;
 * a panel reads/writes the slice it needs. Grows as new geometries ship controls.
 */
export interface ChartTypeConfigPanelProps {
    /** The chart type this panel is configuring. */
    chartType: ChartTypeId;
    /** Disabled until a source table is selected. */
    disabled: boolean;
    /** Quadrant divider editor (used by xy-scatter). */
    quadrant: QuadrantEditorValue;
    onQuadrantChange: (patch: Partial<QuadrantEditorValue>) => void;
    /** Axis-extreme label editor (used by xy-scatter: lento/rápido style). */
    axisEndLabels: AxisEndLabelsEditorValue;
    onAxisEndLabelsChange: (patch: Partial<AxisEndLabelsEditorValue>) => void;
}

/**
 * Type-aware copy for the shared decorations editor. Some types reuse the same
 * bands/cutoffs/scale controls to mean different things (gauge/bullet: the bands
 * ARE the colored zones, the scale IS the value range), so the labels adapt per
 * type instead of always reading "Faixas de referência / Escala do eixo Y".
 * Anything omitted falls back to the generic cartesian wording.
 */
export interface ChartTypeDecorationsCopy {
    sectionTitle?: string;
    bandsLabel?: string;
    cutoffLabel?: string;
    scaleLabel?: string;
    /** Short helper under the section title, e.g. explaining zones for gauge. */
    hint?: string;
}

export interface ChartTypeDefinition {
    id: ChartTypeId;
    label: string;
    icon: IconComponent;
    /** Miniature type sketch shown on the gallery card. */
    Thumbnail: FC<SVGProps<SVGSVGElement>>;
    supports: ChartTypeSupports;
    /** Overrides the generic decorations-editor wording for this type. */
    decorationsCopy?: ChartTypeDecorationsCopy;
    /** Extra per-type controls rendered inside the modal. Unused today. */
    ConfigPanel?: ComponentType<ChartTypeConfigPanelProps>;
}

/**
 * Total map over `ChartTypeId` — the `Record` type forces every id to be
 * registered, so a new type cannot be half-added (mirrors the core's total
 * GEOMETRY map). Declaration order is the picker order.
 */
export const CHART_TYPE_REGISTRY: Record<ChartTypeId, ChartTypeDefinition> = {
    bar: {
        id: 'bar',
        label: 'Barras',
        icon: Chart,
        Thumbnail: BarsThumb,
        supports: {
            multiSeries: true,
            decorations: true,
            seriesLabel: true,
            grouping: true,
        },
    },
    line: {
        id: 'line',
        label: 'Linhas',
        icon: Diagram,
        Thumbnail: LineThumb,
        supports: {
            multiSeries: true,
            decorations: true,
            seriesLabel: true,
            grouping: true,
        },
    },
    radar: {
        id: 'radar',
        label: 'Radar',
        icon: Radar2,
        Thumbnail: RadarThumb,
        // Radar is radial-only: no cartesian y scale to hang bands/cutoffs off.
        supports: { multiSeries: true, decorations: false, seriesLabel: true },
    },
    scatter: {
        id: 'scatter',
        label: 'Dispersão',
        icon: Category,
        Thumbnail: ScatterThumb,
        supports: { multiSeries: true, decorations: true, seriesLabel: true },
    },
    xy_scatter: {
        id: 'xy_scatter',
        label: 'Dispersão (XY)',
        icon: Grid2,
        Thumbnail: QuadrantThumb,
        // True {x,y}: one numeric X + one numeric Y column, no series/bands;
        // quadrant dividers are configured in its ConfigPanel.
        supports: {
            multiSeries: false,
            decorations: false,
            seriesLabel: false,
        },
        ConfigPanel: XyScatterConfigPanel,
    },
    distribution: {
        id: 'distribution',
        label: 'Curva normal',
        icon: Graph,
        Thumbnail: NormalCurveThumb,
        // One descriptive X + one numeric score Y; each score becomes a marker
        // on the standard normal curve. The classification tones derive from a
        // single background color (StyleStep's distribution control), so there's
        // no cartesian-y decorations editor here.
        supports: {
            multiSeries: false,
            decorations: false,
            seriesLabel: false,
        },
    },
    gauge: {
        id: 'gauge',
        label: 'Medidor',
        icon: Speedometer,
        Thumbnail: GaugeThumb,
        // A single value on an arc. Reuses the decorations editor: reference
        // bands become the colored zones and the y-scale min/max sets the range.
        supports: { multiSeries: false, decorations: true, seriesLabel: false },
        decorationsCopy: {
            sectionTitle: 'Zonas e faixa do medidor',
            hint: 'As faixas viram as zonas coloridas do medidor. Só o primeiro valor da tabela é usado.',
            bandsLabel: 'Zonas (faixas coloridas)',
            cutoffLabel: 'Marcadores de meta',
            scaleLabel: 'Faixa de valores (mín/máx)',
        },
    },
    bullet: {
        id: 'bullet',
        label: 'Barra de nível',
        icon: Chart21,
        Thumbnail: BulletThumb,
        // A single value on a horizontal level bar. Same reuse as gauge:
        // reference bands become the zones, the y-scale min/max the range.
        supports: { multiSeries: false, decorations: true, seriesLabel: false },
        decorationsCopy: {
            sectionTitle: 'Zonas e faixa do nível',
            bandsLabel: 'Zonas (faixas coloridas)',
            cutoffLabel: 'Marcadores de meta',
            scaleLabel: 'Faixa de valores (mín/máx)',
        },
    },
    level_bars: {
        id: 'level_bars',
        label: 'Classificação por linha',
        icon: RowVertical,
        Thumbnail: ClassificationThumb,
        // The multi-row sibling of bullet: EVERY table row gets its own level
        // bar over shared classification zones (Vineland domains, facetas).
        supports: { multiSeries: false, decorations: true, seriesLabel: false },
        decorationsCopy: {
            sectionTitle: 'Zonas e faixa das barras',
            hint: 'As faixas viram as zonas coloridas. Cada linha da tabela vira uma barra.',
            bandsLabel: 'Zonas (faixas coloridas)',
            cutoffLabel: 'Marcadores de meta',
            scaleLabel: 'Faixa de valores (mín/máx)',
        },
    },
};

/** Registry entries in declaration order, for the modal's type picker. */
export const CHART_TYPE_LIST: ChartTypeDefinition[] = Object.values(CHART_TYPE_REGISTRY);

/** Safe lookup: falls back to `bar` so a legacy/out-of-registry id can't crash the modal. */
export const getChartTypeDefinition = (chartType: string): ChartTypeDefinition =>
    CHART_TYPE_REGISTRY[chartType as ChartTypeId] ?? CHART_TYPE_REGISTRY.bar;
